/* eslint-disable react-hooks/rules-of-hooks */
import React, { FC, useCallback, useMemo } from 'react';
import { ProductCarousel } from '@ldp-web/component-library/ui/organisms/product-carousel';
import { useFeatureFlags, useSettingsContext } from '@ldp/common-contexts';
import { useSDMCartContext, useSDMRouterContext } from '@ldp/sdm-contexts';
import {
  TSDMComponentAnalyticsObject,
  trackMediaAnalyticsEvent,
  useAddToCartTrackEvent,
} from '@ldp/sdm-analytics-utils';
import {
  IComponentCommonProps,
  IComponentIdProps,
  IHeaderGroupComponentProps,
  ISDMProductTileData,
  SDMLanguageType,
  TSDMCta,
  PCXLanguageType,
} from '@ldp/common-types';
import { useSimpleTranslation } from '@ldp/simple-i18n';
import { useGetA11yCartMessages } from '../../hooks';
import { useTrackMediaAnalyticsAdClick } from '@ldp/media-analytics';
import { HeaderGroupComponent } from '../header-group/header-group.component';
import { WrapperComponent } from '../wrapper';
import { updateProductTiles, useRedirectLink } from '../../utils';

export interface IProductCarouselComponentProps extends IComponentCommonProps, IComponentIdProps {
  /** Current Language */
  language?: SDMLanguageType;
  /** Title heading for Carousel */
  headline: string;
  /** Subtitle */
  subtitle_text?: string;
  /** Link Details */
  callToAction: TSDMCta;
  /** Product Tiles Data */
  productTiles: ISDMProductTileData[];
  /** translation for skip used in skip link */
  i18nskip?: string;
  /** line of business */
  lob?: string;
  /** header group details */
  headerGroup?: IHeaderGroupComponentProps;
  /** analytics object */
  analytics?: TSDMComponentAnalyticsObject;
}

// SSR checks
const IS_SSR = typeof window === 'undefined';

/** Renders Product carousel component */
export const ProductCarouselComponent: FC<IProductCarouselComponentProps> = (props) => {
  const { headline, productTiles, callToAction, headerGroup, styling, analytics, contentId, id } = props;
  const { addToCart, entries, isLoading: isCartLoading } = useSDMCartContext();

  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow } = getFeatureFlags();
  const settings = useSettingsContext();
  const trackMediaAnalyticsAdClick = useTrackMediaAnalyticsAdClick();
  const trackFn = !IS_SSR ? useAddToCartTrackEvent(EnableHeliosSnowplow) : () => {};
  const router = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);

  const compData = useMemo(
    () => ({
      id: contentId || null,
      type: props.componentId || null,
      name: headline || headerGroup?.headline?.text || '',
    }),
    [contentId, props.componentId, headline]
  );
  const genericAnalytics = analytics?.genericAnalytics;

  const dsModelAnalyticContext = useMemo(() => {
    return genericAnalytics?.ds_model ?? analytics?.dsModel ?? [];
  }, [genericAnalytics?.ds_model, analytics?.dsModel]);
  const carouselPosition = analytics?.position;

  const i18n = useSimpleTranslation();
  const a11yCartMessages = useGetA11yCartMessages();

  const updatedProductTiles = updateProductTiles(productTiles, entries);

  // Create a wrapper over the tracking function to pass productTiles
  const trackFnWrapper = useCallback(
    (type: string, context: unknown): void => {
      trackMediaAnalyticsEvent(
        type,
        context,
        updatedProductTiles,
        compData,
        EnableHeliosSnowplow,
        trackFn,
        trackMediaAnalyticsAdClick,
        undefined,
        dsModelAnalyticContext,
        genericAnalytics,
        carouselPosition
      );
    },
    [updatedProductTiles, compData, EnableHeliosSnowplow]
  );

  const headerGroupProps: IHeaderGroupComponentProps = headerGroup ?? {
    headline: {
      text: headline,
      variantId: 'MEDIUM_03',
      callToAction: callToAction,
      clickHandler: redirectToPage,
    },
  };

  return (
    <>
      <WrapperComponent isExtraLargeSpacing styling={styling} id={id}>
        <HeaderGroupComponent noWrapper {...headerGroupProps} />
        <ProductCarousel
          skipLinkLabel={headline}
          product_tiles={updatedProductTiles as any}
          entries={entries}
          addToCartCB={addToCart}
          isCartLoading={isCartLoading}
          trackingFn={trackFnWrapper}
          productTileAction={redirectToPage}
          i18nskip={i18n('skip')}
          a11yMessages={a11yCartMessages}
          language={settings.locale as PCXLanguageType}
          lob="sdm"
        />
      </WrapperComponent>
    </>
  );
};

export default ProductCarouselComponent;
